/**
 * CRM API client — talks to the Wisedrive CRM backend.
 *
 * Contract (agreed with CRM team):
 *   GET    /api/public/packages?country_code=IN
 *   POST   /api/public/orders/create
 *   POST   /api/public/orders/verify
 *   (webhook /api/public/webhooks/razorpay lives server-side, not called from here)
 *
 * Auth: every request sends header `x-wd-site-key: <REACT_APP_WD_SITE_KEY>`.
 * All Razorpay secrets stay in CRM .env — the website never handles them.
 */

import axios from "axios";
import { getCrmOverride } from "../utils/envOverride";

const ENV_BASE = process.env.REACT_APP_CRM_API_URL || "";
const ENV_KEY = process.env.REACT_APP_WD_SITE_KEY || "";

/**
 * Resolves the active CRM endpoint + site key at call time.
 * Override from localStorage (set via ?crm_url=...&crm_key=... URL param)
 * takes precedence; otherwise falls back to compile-time env vars (prod).
 */
function resolveCrm() {
  const ov = getCrmOverride();
  if (ov) return { baseURL: ov.url, siteKey: ov.key, env: ov.env };
  return { baseURL: ENV_BASE, siteKey: ENV_KEY, env: 'prod' };
}

export const isCrmConfigured = () => {
  const { baseURL, siteKey } = resolveCrm();
  return Boolean(baseURL && siteKey);
};

/**
 * Extracts a *string* error message from an axios error.
 * Handles FastAPI 422 where `detail` is a Pydantic array of error objects
 * (keys: type, loc, msg, input, ctx, url).
 */
export function toErrorMessage(e, fallback = 'Something went wrong') {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((d) => d?.msg || String(d)).join('; ');
  }
  if (detail && typeof detail === 'object' && detail.msg) return String(detail.msg);
  if (typeof e?.message === 'string' && e.message) return e.message;
  return fallback;
}

/**
 * Returns true if `code` looks like a valid 2-letter country code.
 */
export const isValidCountryCode = (code) =>
  typeof code === 'string' && /^[A-Za-z]{2}$/.test(code);

const client = axios.create({
  timeout: 20000,
});

// Every request picks up the current CRM base URL + site key at send time,
// so toggling env via the URL override is instant (no axios instance rebuild).
client.interceptors.request.use((config) => {
  const { baseURL, siteKey } = resolveCrm();
  config.baseURL = baseURL;
  config.headers = config.headers || {};
  config.headers['Content-Type'] = 'application/json';
  config.headers['x-wd-site-key'] = siteKey;
  return config;
});

// ---- Packages ----------------------------------------------------------

/**
 * Returns packages sorted by `order` ascending + filtered by is_active when present.
 * Also surfaces country meta (currency symbol, name) from the wrapped response.
 *
 * @param {string} countryCode e.g. "IN", "MY", "TH", "PH"
 * @returns {Promise<{ country:object, packages:Array, count:number }>}
 */
export async function fetchInspectionPackages(countryCode) {
  // Guard against malformed routes like "/i", "/report", etc. — always hit
  // CRM with a clean 2-letter code, defaulting to "IN".
  const normalized = isValidCountryCode(countryCode)
    ? countryCode.toUpperCase()
    : 'IN';
  const res = await client.get("/api/public/packages", {
    params: { country_code: normalized },
  });
  const { country = null, packages = [], count = 0 } = res.data || {};
  const sorted = [...packages]
    .filter((p) => p.is_active !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return { country, packages: sorted, count };
}

// ---- Orders ------------------------------------------------------------

/**
 * Normalises an India 10-digit phone into E.164 (+91XXXXXXXXXX).
 * If the user already typed a leading +, it's preserved as-is.
 */
export function toE164India(raw) {
  if (!raw) return '';
  const s = String(raw).trim();
  if (s.startsWith('+')) return s.replace(/\s+/g, '');
  const digits = s.replace(/\D/g, '');
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`;
  if (digits.length === 11 && digits.startsWith('0')) return `+91${digits.slice(1)}`;
  return digits ? `+${digits}` : '';
}

/**
 * Creates a pending order in CRM + a Razorpay order. No secrets returned.
 * Request shape per CRM contract (OrderCreateRequest):
 *   { package_id, country_code, customer:{name,phone,email?}, city?, notes? }
 *
 * Returns:
 *   { order_id, rzp_order_id, rzp_key_id, amount (paise), amount_display (rupees),
 *     currency, currency_symbol, package, customer }
 */
export async function createOrder(payload) {
  const { data } = await client.post("/api/public/orders/create", payload);
  return data;
}

/**
 * Verifies Razorpay signature server-side, marks order paid, triggers
 * CRM's WhatsApp ops-notification. Idempotent on the server.
 *
 * @param {{order_id:string, razorpay_payment_id:string, razorpay_order_id:string, razorpay_signature:string}} payload
 * @returns {Promise<{success:boolean, order:object, already_paid?:boolean}>}
 */
export async function verifyOrder(payload) {
  const { data } = await client.post("/api/public/orders/verify", payload);
  return data;
}

/**
 * Verifies with up to 3 total attempts for transient network / 5xx failures.
 * Permanent 4xx errors (signature_failed, mismatch, not_found) are NOT retried.
 * Backoff: 0ms, 500ms, 1500ms.
 */
export async function verifyOrderWithRetry(payload) {
  const delays = [0, 500, 1500];
  let lastErr;
  for (const d of delays) {
    if (d > 0) await new Promise((r) => setTimeout(r, d));
    try {
      return await verifyOrder(payload);
    } catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      const isTransient = !status || status >= 500 || status === 408 || status === 429;
      if (!isTransient) throw e; // permanent error — bail immediately
    }
  }
  throw lastErr;
}

// ---- OTP (Phase 2.1) ---------------------------------------------------

/**
 * Sends a 6-digit OTP via SMS (Fast2SMS on CRM side).
 * Test phone +919999999999 bypasses SMS on staging; universal OTP = 123456.
 * @param {{phone:string, country_code?:string, purpose?:string}} payload
 * @returns {Promise<{success:boolean, request_id:string, expires_in_seconds:number, channel:string, masked_phone:string}>}
 */
export async function sendOtp({ phone, country_code = 'IN', purpose = 'inspection_booking' }) {
  const { data } = await client.post('/api/public/otp/send', {
    phone,
    country_code,
    purpose,
  });
  return data;
}

/**
 * Verifies a 6-digit OTP against an active request_id. On success returns a
 * single-use, short-lived JWT (verification_token) that must be forwarded
 * into /orders/create to prove phone ownership.
 * @returns {Promise<{success:boolean, verification_token:string, phone:string, expires_at:string}>}
 */
export async function verifyOtp({ request_id, otp }) {
  const { data } = await client.post('/api/public/otp/verify', {
    request_id,
    otp,
  });
  return data;
}

/**
 * Re-sends the OTP for the same request_id. 30s cooldown enforced server-side.
 */
export async function resendOtp({ request_id }) {
  const { data } = await client.post('/api/public/otp/resend', { request_id });
  return data;
}

// ---- Vehicle lookup ----------------------------------------------------

/**
 * Looks up vehicle details by registration number (India only in Phase 2).
 * Returns canned data on staging for KA00TEST01 / KA00TEST02; real reg numbers
 * hit Surepass with 24h Mongo cache.
 * @returns {Promise<{success:boolean, vehicle:object, source:string}>}
 */
export async function lookupVehicle({ reg_no, country_code = 'IN' }) {
  const { data } = await client.get('/api/public/vehicles/lookup', {
    params: { reg_no: String(reg_no || '').trim().toUpperCase(), country_code },
  });
  return data;
}

export default {
  isCrmConfigured,
  fetchInspectionPackages,
  createOrder,
  verifyOrder,
  verifyOrderWithRetry,
  sendOtp,
  verifyOtp,
  resendOtp,
  lookupVehicle,
  toE164India,
  toErrorMessage,
};
